import { z } from 'zod';
import { DateTimeSchema, ObjectIdSchema, ObjectIdString, OpportunityType } from '../common.js';
import { CandidateStage, InquiryType, ReasonForLoss, RiskSignal } from '../llmResponseSchemas.js';

//--------------------------------------------------------------------------------------------------------------------//
//region Analytics Base Schema

export const AggregatedAnalyticsType = z.enum(['strategicThemes']);

export const AnalyticsCategory = z.enum(['opportunity', 'aggregated']);
export type AnalyticsCategory = z.infer<typeof AnalyticsCategory>;

export type AggregatedAnalyticsType = z.infer<typeof AggregatedAnalyticsType>;

const opportunityAnalyticsTypes = [
  'lossToCompetitor',
  'followUpFailure',
  'placementBackOutRisk',
] as const satisfies OpportunityType[];

export const OpportunityAnalyticsType = z.enum(opportunityAnalyticsTypes);
export type OpportunityAnalyticsType = z.infer<typeof OpportunityAnalyticsType>;
export const AnalyticsType = z.enum([
  ...AggregatedAnalyticsType.options,
  ...OpportunityAnalyticsType.options,
]);
export type AnalyticsType = z.infer<typeof AnalyticsType>;

export const AnalyticsStatus = z.enum([
  'new',
  'verifying',
  'complete',
  'error',
  'pending_dependencies',
]);
export type AnalyticsStatus = z.infer<typeof AnalyticsStatus>;

export const SamplingData = z
  .object({
    samplingApplied: z.boolean(),
    // Additional notes about sampling
    samplingNote: z.string(),
    originalCount: z.number(),
    sampledCount: z.number(),
    samplingRatio: z.number(),
  })
  .partial();
export type SamplingData = z.infer<typeof SamplingData>;

// Base schema
export const IAnalyticsBase = z.object({
  _id: ObjectIdSchema,
  account: ObjectIdSchema.or(ObjectIdString),
  status: AnalyticsStatus,
  category: AnalyticsCategory,
  type: AnalyticsType,
  // Query parameters used to generate this analytics
  queryParams: z.object({
    startDate: DateTimeSchema,
    endDate: DateTimeSchema,
    otherParams: z.record(z.string(), z.unknown()).nullish(),
  }),
  processingErrors: z.string().array().nullish(),
  llmResponse: ObjectIdSchema.or(ObjectIdString).nullish(),

  // Output type will be overridden by specific analytics types
  output: z.object({
    insights: z.string().array().nullish(),
  }),

  // Sampling metadata for token-limited analytics
  metadata: z.object({
    sampling: SamplingData.nullish(),
  }),

  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IAnalyticsBase = z.infer<typeof IAnalyticsBase>;

export const AnalyticsBaseDTO = z.object({
  ...IAnalyticsBase.shape,
  _id: ObjectIdString,
  account: ObjectIdString,
  llmResponse: ObjectIdString.nullish(),
});
export type AnalyticsBaseDTO = z.infer<typeof AnalyticsBaseDTO>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Opportunity Analytics

export const VolumeData = z.object({
  period: z.string(),
  created: z.number(),
  completed: z.number(),
});
export type VolumeData = z.infer<typeof VolumeData>;

export const ManagerData = z.object({
  name: z.string(),
  open: z.number(),
  closed: z.number(),
});
export type ManagerData = z.infer<typeof ManagerData>;

export const RecruiterData = z.object({
  name: z.string(),
  noActionNeeded: z.number(),
  managerResolved: z.number(),
  unresolved: z.number(),
});
export type RecruiterData = z.infer<typeof RecruiterData>;

export const AnalyticsChartData = z.object({
  volumeData: VolumeData.array(),
  managerData: ManagerData.array(),
  recruiterData: RecruiterData.array(),
});
export type AnalyticsChartData = z.infer<typeof AnalyticsChartData>;

export const AnalyticsStats = z.object({
  totalDetected: z.number(),
  currentlyOpen: z.number(),
  avgResolutionTime: z.number(),
  completionRate: z.number(),
});
export type AnalyticsStats = z.infer<typeof AnalyticsStats>;

export const OpportunityAnalyticsOutputBase = z.object({
  ...IAnalyticsBase.shape.output.shape,
  chartData: AnalyticsChartData.nullish(),
  stats: AnalyticsStats.nullish(),
  summary: z.record(z.string(), z.unknown()).nullish(),
  opportunityOutput: z.object({}).nullish(),
});
export type OpportunityAnalyticsOutputBase = z.infer<typeof OpportunityAnalyticsOutputBase>;

export const IOpportunityAnalyticsBase = z.object({
  ...IAnalyticsBase.shape,
  category: z.literal('opportunity'),
  type: OpportunityType,
  output: OpportunityAnalyticsOutputBase,
});
export type IOpportunityAnalyticsBase = z.infer<typeof IOpportunityAnalyticsBase>;

export const OpportunityAnalyticsBaseDTO = z.object({
  ...AnalyticsBaseDTO.shape,
  category: z.literal('opportunity'),
  type: OpportunityType,
  output: OpportunityAnalyticsOutputBase,
});
export type OpportunityAnalyticsBaseDTO = z.infer<typeof OpportunityAnalyticsBaseDTO>;

// Competitor specific data
export const CompetitorBreakdownItem = z.object({
  name: z.string(),
  category: z.string(),
  opportunityIds: ObjectIdString.array(),

  // Two-tier reason breakdown from LLM (themes containing detailed reasons)
  reasonBreakdown: z
    .object({
      theme: ReasonForLoss.or(z.string()), // support any string for legacy Loss Reasons,
      opportunityIds: ObjectIdString.array(),
      // TODO: remove detailedReasons???
      detailedReasons: z
        .object({
          reason: z.string(),
          opportunityIds: ObjectIdString.array(),
        })
        .array(),
    })
    .array(),
});
export type CompetitorBreakdownItem = z.infer<typeof CompetitorBreakdownItem>;

const OpportunitySubset = z.object({
  name: z.string(),
  opportunityIds: ObjectIdString.array(),
});

export const LossToCompetitorSummary = z.object({
  totalLosses: z.number(), // maybe can remove this
  leadingCompetitor: OpportunitySubset,
  unknownAgencies: OpportunitySubset,
  directStaffPositions: OpportunitySubset,
});
export type LossToCompetitorSummary = z.infer<typeof LossToCompetitorSummary>;

const ILossToCompetitorAnalytics = z.object({
  ...IOpportunityAnalyticsBase.shape,
  type: z.literal('lossToCompetitor'),
  output: z.object({
    ...IOpportunityAnalyticsBase.shape.output.shape,

    opportunityOutput: z
      .object({
        competitorBreakdown: CompetitorBreakdownItem.array().nullish(),
      })
      .nullish(),
    summary: LossToCompetitorSummary.nullish(),
  }),
});

const RecruiterBreakdownItem = z.object({
  name: z.string(),
  totalOpportunities: z.number(),
  candidateStatuses: z
    .object({
      name: CandidateStage.or(z.string()), // support any string for legacy Candidate Stages,
      inquiryTypes: z
        .object({
          name: InquiryType.or(z.string()), // support any string for legacy Inquiry Types,
          opportunityIds: ObjectIdString.array(),
        })
        .array(),
    })
    .array(),
});
export type RecruiterBreakdownItem = z.infer<typeof RecruiterBreakdownItem>;

export const FollowUpFailureSummary = z.object({
  averageDelay: z.number(),
  timeSensitiveInquiries: OpportunitySubset,
  onAssignmentInquiries: OpportunitySubset,
});
export type FollowUpFailureSummary = z.infer<typeof FollowUpFailureSummary>;

const UrgencyLevel = z.enum(['Low', 'Medium', 'Urgent']);
export type UrgencyLevel = z.infer<typeof UrgencyLevel>;

const FollowUpFailureAnalyticsOutput = z.object({
  opportunityOutput: z
    .object({
      recruiterBreakdown: RecruiterBreakdownItem.array().nullish(),
      urgencyBreakdown: z
        .object({
          name: UrgencyLevel,
          value: z.number(),
        })
        .array()
        .nullish(),
    })
    .nullish(),

  summary: FollowUpFailureSummary.nullish(),
});

export const RiskBreakdownItem = z.object({
  name: z.string(),
  total: z.number(),
  riskSignals: z
    .object({
      name: RiskSignal.or(z.string()), // support any string for legacy Risk Signals
      opportunityIds: ObjectIdString.array(),
    })
    .array(),
});
export type RiskBreakdownItem = z.infer<typeof RiskBreakdownItem>;

export const BackOutRiskSummary = z.object({
  highRisk: OpportunitySubset,
  mostCommonRiskSignal: OpportunitySubset,
  averageDaysBeforeStart: z.number(),
});
export type BackOutRiskSummary = z.infer<typeof BackOutRiskSummary>;

const PlacementBackOutRiskAnalyticsOutput = z.object({
  opportunityOutput: z
    .object({
      facilityRiskBreakdown: RiskBreakdownItem.array().nullish(),
      recruiterRiskBreakdown: RiskBreakdownItem.array().nullish(),
    })
    .nullish(),
  summary: BackOutRiskSummary.nullish(),
});

// export const IOpportunityAnalytics = z.discriminatedUnion('type', mapValues(opportunityAnalyticsOutputs, (val, key)) => opportunityAnalyticsSubType(key, val));

export const IOpportunityAnalytics = z.discriminatedUnion('type', [
  ILossToCompetitorAnalytics,
  // TODO: extract these
  z.object({
    ...IOpportunityAnalyticsBase.shape,
    type: z.literal('followUpFailure'),
    output: z.object({
      ...IOpportunityAnalyticsBase.shape.output.shape,
      ...FollowUpFailureAnalyticsOutput.shape,
    }),
  }),
  z.object({
    ...IOpportunityAnalyticsBase.shape,
    type: z.literal('placementBackOutRisk'),
    output: z.object({
      ...IOpportunityAnalyticsBase.shape.output.shape,
      ...PlacementBackOutRiskAnalyticsOutput.shape,
    }),
  }),
]);
export type IOpportunityAnalytics<T extends OpportunityAnalyticsType = OpportunityAnalyticsType> =
  T extends OpportunityAnalyticsType ? z.infer<typeof IOpportunityAnalytics> & { type: T } : never;

export const OpportunityAnalyticsDTO = IOpportunityAnalytics.transform(({ _id, ...rest }) => ({
  _id: _id.toString(),
  ...rest,
}));
// export const OpportunityAnalyticsDTO = z.discriminatedUnion('type', [
// z.object({
//     ...OpportunityAnalyticsBaseDTO.shape,
//     type: z.literal('lossToCompetitor'),
//     output: z.object({
//         ...OpportunityAnalyticsBaseDTO.shape.output.shape,
//         ...LossToCompetitorAnalyticsOutput.shape,
//     }),
// }),
// z.object({
//     ...OpportunityAnalyticsBaseDTO.shape,
//     type: z.literal('followUpFailure'),
//     output: z.object({
//         ...OpportunityAnalyticsBaseDTO.shape.output.shape,
//         ...FollowUpFailureAnalyticsOutput.shape,
//     }),
// }),
// z.object({
//     ...OpportunityAnalyticsBaseDTO.shape,
//     type: z.literal('placementBackOutRisk'),
//     output: z.object({
//         ...OpportunityAnalyticsBaseDTO.shape.output.shape,
//         ...PlacementBackOutRiskAnalyticsOutput.shape,
//     }),
// }),
// ]);
export type OpportunityAnalyticsDTO<T extends OpportunityAnalyticsType = OpportunityAnalyticsType> =
  z.infer<typeof OpportunityAnalyticsDTO> & { type: T };

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Aggregated Analytics

export const IAggregatedAnalyticsBase = z.object({
  ...IAnalyticsBase.shape,
  category: z.literal('aggregated'),
  type: AggregatedAnalyticsType,
  sourceAnalytics: z.record(z.string(), ObjectIdSchema.optional()).nullish(),

  // For meta-analytics that depend on other analytics
  // TODO: structure this differently?
  //   maybe just rename "inputAnalytics" or something
  dependencyAnalyticsIds: ObjectIdSchema.array(),
});
export type IAggregatedAnalyticsBase = z.infer<typeof IAggregatedAnalyticsBase>;

export const ThemeData = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  priority: z.enum(['High', 'Medium', 'Low', 'Low-Medium']),
  color: z.string(),

  metrics: z
    .object({
      name: z.string(),
      value: z.string(),
      target: z.string().optional(),
      status: z.enum(['critical', 'warning', 'success']),
    })
    .array(),

  trendData: z.array(
    z.object({
      month: z.string(),
      value: z.number(),
    })
  ),

  contributingFactors: z
    .object({
      name: z.string(),
      value: z.number(),
      link: z.string(),
    })
    .array(),

  recommendation: z.string(),
  actionItems: z.string().array(),
  impact: z.string(),
  timeToImplement: z.string(),
});
export type ThemeData = z.infer<typeof ThemeData>;

export const IStrategicThemesAnalytics = z.object({
  ...IAggregatedAnalyticsBase.shape,
  type: z.literal('strategicThemes'),

  // TODO: restructure/remove this
  sourceAnalytics: z.object({
    competitor: ObjectIdSchema.optional(),
    followUp: ObjectIdSchema.optional(),
  }),

  output: z
    .object({
      ...IAggregatedAnalyticsBase.shape.output.shape,

      themes: ThemeData.array(),
      summary: z.object({
        totalThemes: z.number(),
        priorityDistribution: z.object({
          high: z.number(),
          medium: z.number(),
          low: z.number(),
        }),
      }),
    })
    .catchall(z.unknown().optional()),
});
export type IStrategicThemesAnalytics = z.infer<typeof IStrategicThemesAnalytics>;

export const AnalyticsResponse = z.object({
  currentPeriod: OpportunityAnalyticsDTO,
  previousPeriod: OpportunityAnalyticsDTO,
});
export type AnalyticsResponse<T extends OpportunityAnalyticsType = OpportunityAnalyticsType> =
  T extends any ?
    {
      currentPeriod: OpportunityAnalyticsDTO<T>;
      previousPeriod: OpportunityAnalyticsDTO<T>;
    }
  : never;

//endregion
//--------------------------------------------------------------------------------------------------------------------//

// export const IAnalyticsAny = z.discriminatedUnion('type', [
//     ILossToCompetitorAnalytics,
//     IFollowUpFailureAnalytics,
//     IPlacementBackOutRiskAnalytics,
//     IStrategicThemesAnalytics,
// ]);
// export type IAnalyticsAny = z.infer<typeof IAnalyticsAny>;
